'use client';

import React, { useMemo } from 'react';
import { VisualizationWrapper } from './visualization-wrapper';

interface ConceptMapProps {
  element: {
    props: {
      title: string;
      centralConcept: string;
      concepts: Array<{
        id: string;
        label: string;
        description?: string;
        level: number;
      }>;
      relationships: Array<{
        from: string;
        to: string;
        label: string;
      }>;
    };
    raw?: boolean;
    visualizationId?: string;
  };
}

// Simple hardcoded colors — no theme dependency
const levelColors = [
  { fill: '#6366f1', text: '#ffffff' }, // indigo
  { fill: '#3b82f6', text: '#ffffff' }, // blue
  { fill: '#8b5cf6', text: '#ffffff' }, // violet
  { fill: '#ec4899', text: '#ffffff' }, // pink
];

const centralColor = { fill: '#1e293b', text: '#ffffff' };

interface NodePosition {
  id: string;
  x: number;
  y: number;
  label: string;
  description?: string;
  level: number;
  width: number;
  height: number;
}

function computeLayout(
  centralConcept: string,
  concepts: ConceptMapProps['element']['props']['concepts'],
) {
  const cx = 500;
  const cy = 350;
  const nodes: NodePosition[] = [];

  const centralWidth = Math.max(120, centralConcept.length * 10 + 40);
  nodes.push({
    id: 'central',
    x: cx,
    y: cy,
    label: centralConcept,
    level: -1,
    width: centralWidth,
    height: 44,
  });

  const byLevel: Record<number, typeof concepts> = {};
  for (const c of concepts) {
    const lvl = c.level;
    if (!byLevel[lvl]) byLevel[lvl] = [];
    byLevel[lvl].push(c);
  }

  const levels = Object.keys(byLevel)
    .map(Number)
    .sort((a, b) => a - b);

  for (const lvl of levels) {
    const group = byLevel[lvl];
    const count = group.length;
    const radius = 140 + lvl * 120;
    const angleStep = (2 * Math.PI) / Math.max(count, 1);
    const angleOffset = lvl * 0.4 - Math.PI / 2;

    for (let i = 0; i < count; i++) {
      const angle = angleOffset + i * angleStep;
      const desc = group[i].description;
      // Wider nodes when descriptions exist, so text wraps into fewer lines
      const nodeWidth = desc
        ? Math.max(160, group[i].label.length * 8 + 32)
        : Math.max(90, group[i].label.length * 8 + 32);
      // Estimate lines needed for description — use conservative 6.5px/char
      // (SVG proportional fonts render wider than monospace estimates)
      const charsPerLine = Math.floor((nodeWidth - 16) / 6.5);
      const descLines = desc ? Math.ceil(desc.length / charsPerLine) : 0;
      // Height: label at center-4, desc starts at center+12, each line +13
      const bottomExtent = descLines > 0 ? 12 + (descLines - 1) * 13 + 16 : 0;
      const topExtent = 16; // label + padding above center
      const nodeHeight = desc ? Math.max(50, 2 * Math.max(topExtent, bottomExtent)) : 36;
      nodes.push({
        id: group[i].id,
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
        label: group[i].label,
        description: desc,
        level: group[i].level,
        width: nodeWidth,
        height: nodeHeight,
      });
    }
  }

  return nodes;
}

function computeViewBox(nodes: NodePosition[]) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.x - n.width / 2);
    maxX = Math.max(maxX, n.x + n.width / 2);
    minY = Math.min(minY, n.y - n.height / 2);
    maxY = Math.max(maxY, n.y + n.height / 2);
  }
  const pad = 60;
  return {
    x: minX - pad,
    y: minY - pad,
    w: maxX - minX + pad * 2,
    h: maxY - minY + pad * 2,
  };
}

function ConceptMapContent({
  centralConcept,
  concepts,
  relationships,
}: {
  centralConcept: string;
  concepts: ConceptMapProps['element']['props']['concepts'];
  relationships: ConceptMapProps['element']['props']['relationships'];
}) {
  const nodes = useMemo(
    () => computeLayout(centralConcept, concepts),
    [centralConcept, concepts],
  );

  const nodeMap = useMemo(() => {
    const map: Record<string, NodePosition> = {};
    for (const n of nodes) map[n.id] = n;
    return map;
  }, [nodes]);

  const viewBox = useMemo(() => computeViewBox(nodes), [nodes]);

  return (
    <div className="w-full flex items-center justify-center">
      <svg
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        className="w-full"
        style={{ aspectRatio: `${viewBox.w} / ${viewBox.h}`, background: '#ffffff' }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L8,3 L0,6 Z" fill="#94a3b8" />
          </marker>
          <filter id="node-shadow" x="-10%" y="-10%" width="120%" height="130%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>

        {/* Edges */}
        {relationships.map((rel, i) => {
          const from = nodeMap[rel.from];
          const to = nodeMap[rel.to];
          if (!from || !to) return null;

          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const nx = dx / dist;
          const ny = dy / dist;

          const x1 = from.x + nx * (from.width / 2 + 4);
          const y1 = from.y + ny * (from.height / 2 + 4);
          const x2 = to.x - nx * (to.width / 2 + 10);
          const y2 = to.y - ny * (to.height / 2 + 10);

          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2;

          return (
            <g key={i}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#94a3b8"
                strokeWidth="1.5"
                markerEnd="url(#arrowhead)"
              />
              {rel.label && (
                <>
                  <rect
                    x={mx - rel.label.length * 3.2 - 4}
                    y={my - 8}
                    width={rel.label.length * 6.4 + 8}
                    height={16}
                    rx={4}
                    fill="#ffffff"
                    fillOpacity={0.9}
                  />
                  <text
                    x={mx}
                    y={my + 4}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#64748b"
                  >
                    {rel.label}
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const isCentral = node.id === 'central';
          const color = isCentral
            ? centralColor
            : levelColors[node.level] || levelColors[levelColors.length - 1];

          return (
            <g key={node.id} filter="url(#node-shadow)">
              <rect
                x={node.x - node.width / 2}
                y={node.y - node.height / 2}
                width={node.width}
                height={node.height}
                rx={isCentral ? node.height / 2 : 10}
                fill={color.fill}
              />
              <text
                x={node.x}
                y={node.description ? node.y - 4 : node.y + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={isCentral ? 15 : 12}
                fontWeight={isCentral ? 700 : 600}
                fill={color.text}
              >
                {node.label}
              </text>
              {node.description && (() => {
                // Word-wrap description into multiple lines
                const maxChars = Math.floor((node.width - 16) / 5.5);
                const words = node.description.split(' ');
                const lines: string[] = [];
                let current = '';
                for (const word of words) {
                  if (current && (current + ' ' + word).length > maxChars) {
                    lines.push(current);
                    current = word;
                  } else {
                    current = current ? current + ' ' + word : word;
                  }
                }
                if (current) lines.push(current);
                return (
                  <text
                    x={node.x}
                    y={node.y + 12}
                    textAnchor="middle"
                    fontSize={9}
                    fill={color.text}
                    opacity={0.8}
                  >
                    {lines.map((line, li) => (
                      <tspan key={li} x={node.x} dy={li === 0 ? 0 : 13}>{line}</tspan>
                    ))}
                  </text>
                );
              })()}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function ConceptMap({ element }: ConceptMapProps) {
  if (!element?.props?.concepts?.length) {
    return (
      <div className="p-4 border rounded-lg bg-muted/30 text-muted-foreground">
        Loading concept map...
      </div>
    );
  }

  const { title, centralConcept, concepts, relationships } = element.props;

  if (element.raw) {
    return (
      <div className="w-full h-full">
        <ConceptMapContent
          centralConcept={centralConcept}
          concepts={concepts}
          relationships={relationships}
        />
      </div>
    );
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={400}
      flexHeight
      exportData={{
        type: 'concept-map',
        content: element.props,
      }}
      visualizationId={element.visualizationId}
    >
      <ConceptMapContent
        centralConcept={centralConcept}
        concepts={concepts}
        relationships={relationships}
      />
    </VisualizationWrapper>
  );
}
